import { useState, useEffect, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { LegalCompliance } from "@/components/LegalCompliance";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Settings2, Lock, Loader2, Send, Pencil, Radio } from "lucide-react";
import AgentConfig from "./AgentConfig";
import { FeaturesTab } from "./Configuration";
import Advanced from "./Advanced";
import SmsHealthCheck from "@/components/SmsHealthCheck";
import { UsageStatement } from "@/components/UsageStatement";
import { BrandCard } from "@/components/BrandCard";
import { IndustryCard } from "@/components/IndustryCard";
import { AudiencesCard } from "@/components/AudiencesCard";
import { SettingsReadOnly, ViewOnlyPill } from "@/components/SettingsSection";
import { PortalPagesCard } from "@/components/PortalPagesCard";
import { V } from "@/lib/vocab";
import { SettingsSection } from "@/components/SettingsSection";
import { AccessDialCard, DIAL_DEFAULTS } from "@/components/AccessDialCard";

const SUPER_ROLES = ["super_admin", "platform_admin", "onboarding_admin"];
const MANAGER_ROLES = ["company_admin", "regional_manager", "hoa_manager"];
// Plain-language "who can edit this tab" labels, keyed by the delegation-dial level — in the pack's own seat
// titles ("Community Managers", "Administrators"), never internal role keys.
const mgrs = () => `${V.role("hoa_manager")}s`;
const accessLabelFactory = () => ({
  redline: "Ask Redline staff only",
  company: `${V.role("company_admin")}s + Ask Redline`,
  managers: `${mgrs()} and above + Ask Redline`,
  board: `${mgrs()} and above + ${V.lcs("board")} who have the ‘Can configure’ grant + Ask Redline`,
});

// ── 6-tab Information Architecture ──────────────────────────────────────────
// Every setting keeps its ORIGINAL edit-permission dial (assistant/service/modules/advanced);
// only the visual grouping changes. AgentConfig pieces => "assistant" dial, FeaturesTab =>
// "service", Modules => "modules", Advanced => "advanced". Composed per tab below.
const TAB_ORDER = ["concierge", "capabilities", "channels", "policies", "access", "usage", "advanced"];
const TAB_LABELS = {
  concierge: "The Concierge",
  capabilities: "Capabilities & Actions",
  channels: "Channels & Experience",
  policies: "Policies & Standing",
  access: "Access & Team",
  usage: "Usage & Allowances",
  advanced: "Advanced",
};
// Legacy deep-links (?tab=assistant|service|modules|advanced) keep working.
const LEGACY_TAB = { assistant: "concierge", service: "channels", modules: "capabilities", advanced: "advanced" };
// Which dial's "who can edit" note to surface per tab (informational; real gating is per-section).
const TAB_PRIMARY_DIAL = { concierge: "assistant", capabilities: "modules", channels: "service", policies: "assistant", access: "assistant", usage: "assistant", advanced: "advanced" };
// AgentConfig section ids per tab (self-filtered inside AgentConfig via SectionsCtx).
const AGENT_SECTIONS = {
  concierge: ["persona-section", "voice-persona-section", "brain-section", "messages-section", "locale-section"],
  capabilities: ["capabilities-matrix-section", "whitelabel-section"],
  channels: ["voice-calls-section", "unknown-caller-section", "csat-section", "auto-resolve-section", "quick-replies-section", "transcript-display-section"],
  policies: ["guardrails-section", "privacy-modules-section", "disclosure-section", "sms-consent-section"],
  access: ["getting-started-section", "send-window-section"],
  advanced: ["line-health-section"],
};
// FeaturesTab section keys per tab.
const FEATURE_SECTIONS = {
  concierge: ["ai-behaviour"],
  capabilities: ["capabilities"],
  channels: ["billing", "channels"],
  access: ["menu-features"],
};
// Core Concierge, Messages & Announcements, Live Support and Security Monitoring render INSIDE
// "Modules & capabilities" (FeaturesTab) — one list, one place. Their edit rights still follow the "modules" dial.
// One-line "what this tab answers" descriptor shown under each tab title.
const tabDescFactory = () => ({
  concierge: "Who your concierge is — its identity, voice, base AI model and how it learns.",
  capabilities: "What your concierge can do — the skills it performs and what it's allowed to change.",
  channels: `Where ${V.lcs("person")} reach it, how it looks (logo, colour, font), how it handles calls, and how the conversation reads back.`,
  policies: "The rules of engagement — guardrails, privacy, disclosures, consent and escalation.",
  access: "Who can use and edit these settings, plus which sections show in your team's dashboard.",
  usage: `What's included each month, what's been used, any overage — and the ceiling your ${V.grp()} can set.`,
  advanced: "Engineering dials — temperature, retrieval and rate limits. Change only if you know why.",
});
// Group heading rendered above CSAT so review/measurement is visually separate from channels.
const CHANNELS_HEADINGS = { "csat-section": "Insights · review & measurement", "auto-resolve-section": "Conversations · inbox & replies" };

// Shown to managers on Redline-managed sections: full visibility, read-only switches,
// and a one-click way to ask for a change instead of a dead end.
function LockBanner({ tenantName, tenantId, mixed }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  // The click-to-explain toast's "Request a change →" action lands here.
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("rl:request-change", h);
    return () => window.removeEventListener("rl:request-change", h);
  }, []);
  const send = async () => {
    if (!msg.trim()) { toast.error("Tell us what you'd like changed"); return; }
    setSending(true);
    try {
      await api.post(`/tenants/${tenantId}/settings-change-request`, { message: msg.trim() });
      toast.success("Request sent — the Ask Redline team will follow up");
      setOpen(false); setMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the request"); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="settings-lock-banner" className="mb-5 rounded-2xl border border-ink/15 bg-ink text-white px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <Lock className="h-4 w-4 text-white" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-sm">{mixed ? "Some of this tab is managed by Ask Redline" : "Managed by Ask Redline"}</div>
          <div className="text-sm text-white/75">{mixed
            ? <>Cards marked <ViewOnlyPill className="!bg-white !text-ink align-middle" /> are set up by our team — browse them freely; everything else here you can change.</>
            : <>You can see exactly how {tenantName || `this ${V.lc("org")}`} is set up — browse freely, changes go through our team.</>}</div>
        </div>
      </div>
      <button data-testid="settings-request-change-btn" onClick={() => setOpen(true)}
        className="shrink-0 rounded-full bg-white text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-100 transition-colors">
        Request a change
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="settings-request-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a settings change</DialogTitle>
            <DialogDescription>Tell us what you&rsquo;d like turned on, off or adjusted for {tenantName || `your ${V.lc("org")}`} — the Ask Redline team gets it instantly.</DialogDescription>
          </DialogHeader>
          <textarea data-testid="settings-request-message" rows={4} value={msg} onChange={(e) => setMsg(e.target.value)}
            placeholder={`e.g. "Please enable the Amenities module so ${V.lcs("person")} can book the clubhouse."`}
            className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          <button data-testid="settings-request-send" onClick={send} disabled={sending}
            className="w-full rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sending ? "Sending…" : "Send request"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function SettingsPage() {
  const TAB_DESC = tabDescFactory();
  const ACCESS_LABEL = accessLabelFactory();
  const { tenant, tenantId, role, canConfigure, refreshTenants } = useDash();
  const [params, setParams] = useSearchParams();
  const isSuperRole = SUPER_ROLES.includes(role);
  const isManagerRole = MANAGER_ROLES.includes(role);
  // Features / Modules / Advanced sections were never visible to board members
  // (they lived on Redline-managed tabs). Keep that: only super/manager see them.
  const showService = isSuperRole || isManagerRole;

  // Per-section edit rights follow the delegation dial (mirrors server can_edit_settings).
  const levelFor = (s) => (tenant?.settings_access || {})[s] || DIAL_DEFAULTS[s];
  const canEdit = (s) => {
    if (isSuperRole) return true;
    const lvl = levelFor(s);
    if (lvl === "company") return role === "company_admin";
    if (lvl === "managers") return isManagerRole;
    if (lvl === "board") return isManagerRole || (role === "board_member" && canConfigure);
    return false;
  };
  // Why a locked dial is locked for THIS viewer — drives the pill titles, the note and the click-to-explain toast.
  const reasonFor = (s) => {
    if (role !== "board_member" || levelFor(s) === "redline") return "redline";
    return levelFor(s) === "board" ? "board-grant" : "board";
  };

  const requested = params.get("tab") || "concierge";
  const mapped = LEGACY_TAB[requested] || requested;
  const tab = TAB_ORDER.includes(mapped) ? mapped : "concierge";
  const setTab = (v) => setParams(v === "concierge" ? {} : { tab: v }, { replace: true });

  // Keep the active tab visible on the horizontally-scrollable mobile tab strip (e.g. when
  // deep-linked to a later tab like ?tab=advanced, or when switching on a narrow screen).
  const tabsRef = useRef(null);
  useEffect(() => {
    const el = tabsRef.current?.querySelector('[data-state="active"]');
    if (el) el.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [tab]);

  // Read-only shield for locked sections: everything visible, nothing clickable, every card
  // wears a "View only" pill (SettingsReadOnly context) so it's obvious before you try a switch.
  // ONE "Managed by Ask Redline" banner per tab — the first locked block on the tab carries it.
  // Cards stay expandable (browsing is fine); each card makes its own controls inert via the context.
  // Which delegation dials actually appear on a tab (mirrors tabContent below) → "locked" when every
  // card is Redline-managed, "mixed" when only some are, "open" when the viewer can edit all of it.
  const tabDials = (v) => {
    const d = [];
    if (v === "concierge") { d.push("advanced", "assistant"); if (showService) d.push("service"); }
    else if (v === "capabilities") { if (showService) d.push("service", "modules"); d.push("assistant"); }
    else if (v === "channels") { d.push("assistant"); if (showService) d.push("service"); }
    else if (v === "policies") d.push("assistant");
    else if (v === "access") { d.push("assistant"); if (showService) d.push("service"); }
    else if (v === "advanced") { d.push("assistant"); if (showService) d.push("advanced"); }
    return [...new Set(d)];
  };
  const tabLock = (v) => {
    const ds = tabDials(v);
    if (!ds.length) return "none";
    const locked = ds.filter((s) => !canEdit(s)).length;
    return locked === 0 ? "open" : locked === ds.length ? "locked" : "mixed";
  };
  const lockedWrap = (s, node, banner, mixed) => (
    <SettingsReadOnly.Provider value={reasonFor(s)}>
      {banner && <LockBanner tenantName={tenant?.name} tenantId={tenantId} mixed={mixed} />}
      <div data-testid={`settings-locked-${s}`}>{node}</div>
    </SettingsReadOnly.Provider>
  );

  // At-a-glance line computed from the cards ACTUALLY on the tab — never contradicts what's below it.
  const accessNote = (tabId) => {
    const state = tabLock(tabId);
    if (state === "none") return null;
    const dials = tabDials(tabId);
    const primary = TAB_PRIMARY_DIAL[tabId];
    const who = ACCESS_LABEL[levelFor(dials.includes(primary) ? primary : dials[0])] || ACCESS_LABEL.redline;
    const lockedDials = dials.filter((d) => !canEdit(d));
    // A board seat's own limits are the story for board viewers; Redline-managed cards are the footnote.
    const boardSeat = role === "board_member" && lockedDials.some((d) => reasonFor(d) !== "redline");
    const alsoRedline = boardSeat && lockedDials.some((d) => reasonFor(d) === "redline");
    const seatNote = boardSeat && <>{lockedDials.some((d) => reasonFor(d) === "board-grant")
      ? <>A {V.role("hoa_manager")} can turn on “Can configure” for you on the Team page.</>
      : <>Changes here are made by your {V.role("hoa_manager")}s.</>}{alsoRedline && <> Some cards are managed by Ask Redline.</>}</>;
    return (
      <div data-testid={`settings-access-note-${tabId}`} data-state={state}
        className={`mb-4 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-xs ${state === "open" ? "border-emerald-200 bg-emerald-50/60 text-ink/70" : "border-sand-200 bg-white text-ink/70"}`}>
        {state === "open"
          ? <Pencil className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />
          : <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ink/60" strokeWidth={2} />}
        <span>
          {state === "open" && <><span className="font-semibold text-ink">You can edit everything on this tab.</span> Who else can: {who}.</>}
          {state === "locked" && boardSeat && <><span className="font-semibold text-ink">Your board seat is view-only here.</span> Every card is <ViewOnlyPill className="align-middle" /> — browse freely. {seatNote}</>}
          {state === "locked" && !boardSeat && <><span className="font-semibold text-ink">Managed by Ask Redline.</span> Every card here is <ViewOnlyPill className="align-middle" /> — browse freely, changes go through our team via “Request a change”.</>}
          {state === "mixed" && boardSeat && <><span className="font-semibold text-ink">Mixed.</span> Cards marked <ViewOnlyPill className="align-middle" /> are view-only for your board seat; the rest you can change here.</>}
          {state === "mixed" && !boardSeat && <><span className="font-semibold text-ink">Mixed.</span> Cards marked <ViewOnlyPill className="align-middle" /> are managed by Ask Redline; the rest you can change here.</>}
        </span>
      </div>
    );
  };

  const tabContent = (v) => {
    const parts = [];
    let bannerShown = false;
    const mixed = tabLock(v) === "mixed";
    const shield = (s, node) => {
      if (canEdit(s)) return node;
      const banner = !bannerShown && role !== "board_member";
      bannerShown = true;
      return lockedWrap(s, node, banner, mixed);
    };
    // AgentConfig pieces always render (assistant dial) — board keeps read-only view unless granted.
    const agentBlock = (secs, headings) => {
      if (!secs || !secs.length) return null;
      const locked = !canEdit("assistant");
      const banner = locked && !bannerShown && role !== "board_member";
      if (locked) bannerShown = true;
      return (
        <SettingsReadOnly.Provider value={locked ? reasonFor("assistant") : false}>
          {banner && <LockBanner tenantName={tenant?.name} tenantId={tenantId} mixed={mixed} />}
          <AgentConfig embedded sections={secs} sectionHeadings={headings} lockedByDial={locked} />
        </SettingsReadOnly.Provider>
      );
    };
    if (v === "concierge") {
      // Industry pack is Redline-only on the server; Audiences + portal pages follow the concierge dial (iter473).
      parts.push(shield("advanced", <IndustryCard />));
      parts.push(shield("assistant", <AudiencesCard />));
      if (tenant?.features?.portal) parts.push(shield("assistant", <PortalPagesCard />));
      parts.push(agentBlock(AGENT_SECTIONS.concierge));
      if (showService) parts.push(shield("service", <FeaturesTab sections={FEATURE_SECTIONS.concierge} />));
    } else if (v === "capabilities") {
      if (showService) parts.push(shield("service", <FeaturesTab sections={FEATURE_SECTIONS.capabilities} modulesLocked={canEdit("modules") ? false : reasonFor("modules")} />));
      parts.push(agentBlock(AGENT_SECTIONS.capabilities));
    } else if (v === "channels") {
      parts.push(shield("assistant", <BrandCard />));
      parts.push(
        <SettingsSection id="line-health-card" icon={Radio} title={`${V.one("person")} reachability check`}
          summary={`Confirm SMS and voice can actually reach ${V.a("person")}’s number, and auto-fix carrier or opt-out problems.`}>
          <SmsHealthCheck tenantId={tenantId} defaultPhone={tenant?.phone_number || ""} />
        </SettingsSection>
      );
      if (showService) parts.push(shield("service", <FeaturesTab sections={FEATURE_SECTIONS.channels} />));
      parts.push(agentBlock(AGENT_SECTIONS.channels, CHANNELS_HEADINGS));
    } else if (v === "policies") {
      parts.push(shield("assistant", <LegalCompliance tenantId={tenantId} readOnly={!canEdit("assistant")} />));
      parts.push(agentBlock(AGENT_SECTIONS.policies));
    } else if (v === "access") {
      if (isSuperRole) parts.push(<AccessDialCard tenant={tenant} tenantId={tenantId} refreshTenants={refreshTenants} />);
      parts.push(agentBlock(AGENT_SECTIONS.access));
      if (showService) parts.push(shield("service", <FeaturesTab sections={FEATURE_SECTIONS.access} />));
    } else if (v === "usage") {
      parts.push(<UsageStatement tenantId={tenantId} canEdit={isSuperRole || isManagerRole} />);
    } else if (v === "advanced") {
      parts.push(agentBlock(AGENT_SECTIONS.advanced));
      if (showService) parts.push(shield("advanced", <Advanced embedded />));
    }
    return parts.filter(Boolean).map((p, i) => <div key={i}>{p}</div>);
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      <div>
        <PageTitle title={<><Settings2 className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> System Settings</>} about={<>How <span className="font-semibold">{tenant?.name}</span>&rsquo;s concierge behaves and what&rsquo;s turned on — organized into seven areas.</>} />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        {/* Every area stays visible: the strip WRAPS onto a second row instead of scrolling
            sideways and hiding "Advanced" off the right edge (owner, prod screenshot). */}
        <TabsList ref={tabsRef} data-testid="settings-tabs" className="w-full justify-start flex-wrap gap-1 h-auto p-1">
          {TAB_ORDER.map((v) => {
            const st = tabLock(v);
            return (
              <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`} data-lock={st} className="shrink-0 px-3 py-1.5 text-[13px] gap-1.5"
                title={st === "locked" ? (role === "board_member" ? "View only for your seat" : "Managed by Ask Redline — view only") : st === "mixed" ? "Some cards here are view only" : undefined}>
                {TAB_LABELS[v]}
                {st === "locked" && <Lock data-testid={`settings-tab-${v}-lock`} className="h-3 w-3 text-ink/50" strokeWidth={2.25} />}
                {st === "mixed" && <span data-testid={`settings-tab-${v}-mixed`} aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {TAB_ORDER.map((v) => (
          <TabsContent key={v} value={v} className="mt-6 space-y-6">
            <p data-testid={`settings-tab-desc-${v}`} className="text-sm text-muted-foreground -mt-2">{TAB_DESC[v]}</p>
            {accessNote(v)}
            {tabContent(v)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
